import { useMemo, useState, type ChangeEvent } from 'react'
import { useUploadTemplate } from '../../api/templates'
import { useToast } from '../../hooks/useToast'

const GEOJSON_GEOMETRY_TYPES = new Set([
  'Point',
  'LineString',
  'Polygon',
  'MultiPoint',
  'MultiLineString',
  'MultiPolygon',
  'GeometryCollection',
])

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isValidLatitude(value: unknown): value is number {
  return isFiniteNumber(value) && value >= -90 && value <= 90
}

function isValidLongitude(value: unknown): value is number {
  return isFiniteNumber(value) && value >= -180 && value <= 180
}

function isValidLonLatPair(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    isValidLongitude(value[0]) &&
    isValidLatitude(value[1])
  )
}

function isValidBboxArray(value: unknown): boolean {
  if (!Array.isArray(value) || value.length !== 4) return false
  const [minLon, minLat, maxLon, maxLat] = value
  return (
    isValidLongitude(minLon) &&
    isValidLatitude(minLat) &&
    isValidLongitude(maxLon) &&
    isValidLatitude(maxLat) &&
    minLon <= maxLon &&
    minLat <= maxLat
  )
}

function isValidGeoJsonCoordinates(type: string, coordinates: unknown): boolean {
  switch (type) {
    case 'Point':
      return isValidLonLatPair(coordinates)
    case 'MultiPoint':
    case 'LineString':
      return Array.isArray(coordinates) && coordinates.length > 0 && coordinates.every(isValidLonLatPair)
    case 'MultiLineString':
    case 'Polygon':
      return (
        Array.isArray(coordinates) &&
        coordinates.length > 0 &&
        coordinates.every(
          (lineOrRing) =>
            Array.isArray(lineOrRing) &&
            lineOrRing.length > 0 &&
            lineOrRing.every(isValidLonLatPair)
        )
      )
    case 'MultiPolygon':
      return (
        Array.isArray(coordinates) &&
        coordinates.length > 0 &&
        coordinates.every(
          (polygon) =>
            Array.isArray(polygon) &&
            polygon.length > 0 &&
            polygon.every(
              (ring) =>
                Array.isArray(ring) &&
                ring.length > 0 &&
                ring.every(isValidLonLatPair)
            )
        )
      )
    default:
      return false
  }
}

function isValidGeoJson(value: Record<string, unknown>): boolean {
  if (value.type === 'Feature') {
    const geometry = value.geometry
    return Boolean(geometry && typeof geometry === 'object' && isValidGeoJson(geometry as Record<string, unknown>))
  }

  if (value.type === 'FeatureCollection') {
    const features = value.features
    return Array.isArray(features) && features.some((feature) => feature && typeof feature === 'object' && isValidGeoJson(feature as Record<string, unknown>))
  }

  if (typeof value.type !== 'string' || !GEOJSON_GEOMETRY_TYPES.has(value.type)) {
    return false
  }

  if (value.type === 'GeometryCollection') {
    const geometries = value.geometries
    return Array.isArray(geometries) && geometries.length > 0 && geometries.every(
      (geometry) => geometry && typeof geometry === 'object' && isValidGeoJson(geometry as Record<string, unknown>)
    )
  }

  return isValidGeoJsonCoordinates(value.type, value.coordinates)
}

function normalizeTemplateName(input: string): string {
  return input
    .trim()
    .replace(/\.json$/i, '')
    .replace(/[^a-zA-Z0-9_\-]/g, '_')
    .slice(0, 64)
}

function validateTemplateDraft(rawJson: string): { parsed: unknown | null; errors: string[] } {
  const errors: string[] = []
  const trimmed = rawJson.trim()

  if (!trimmed) {
    return { parsed: null, errors: ['Provide JSON content by uploading a file or pasting text.'] }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (err) {
    return { parsed: null, errors: [`Invalid JSON: ${(err as Error).message}`] }
  }

  if (!hasGeospatialLocation(parsed)) {
    errors.push('JSON must include valid geospatial location data (GeoJSON, coordinates, lat/lon, or bbox).')
  }

  return { parsed: errors.length > 0 ? null : parsed, errors }
}

function hasGeospatialLocation(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => hasGeospatialLocation(item))
  }

  if (!value || typeof value !== 'object') {
    return false
  }

  const obj = value as Record<string, unknown>
  const lat = typeof obj.lat === 'number' ? obj.lat : obj.latitude
  const lon = typeof obj.lon === 'number' ? obj.lon : obj.longitude
  if (isValidLatitude(lat) && isValidLongitude(lon)) return true

  if (isValidBboxArray(obj.bbox)) {
    return true
  }

  if (isValidGeoJson(obj)) {
    return true
  }

  if (isValidLonLatPair(obj.coordinates)) {
    return true
  }

  return Object.values(obj).some((child) => hasGeospatialLocation(child))
}

export default function TemplatesTab() {
  const { mutate: uploadTemplate, isPending } = useUploadTemplate()
  const { toast } = useToast()

  const [templateName, setTemplateName] = useState('')
  const [rawJson, setRawJson] = useState('')
  const [fileName, setFileName] = useState<string | null>(null)

  const validation = useMemo(() => validateTemplateDraft(rawJson), [rawJson])
  const normalizedName = normalizeTemplateName(templateName)
  const canSave = Boolean(normalizedName) && Boolean(validation.parsed) && !isPending

  async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const text = await file.text()
      setRawJson(text)
      setFileName(file.name)
      if (!templateName.trim()) {
        setTemplateName(file.name.replace(/\.json$/i, ''))
      }
    } catch {
      toast({ title: 'Failed to read file', variant: 'destructive' })
    } finally {
      event.target.value = ''
    }
  }

  function handleSave() {
    if (!canSave || !validation.parsed) return

    uploadTemplate(
      { name: normalizedName, content: validation.parsed },
      {
        onSuccess: () => {
          toast({ title: 'Template saved' })
          setRawJson('')
          setFileName(null)
        },
        onError: (err) => {
          const message =
            (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
            'Failed to save template'
          toast({ title: message, variant: 'destructive' })
        },
      }
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Upload Raw JSON</h2>

        <div className="space-y-2">
          <label className="text-sm font-medium">Template Name</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="my_custom_template"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <p className="text-xs text-muted-foreground">Saved as: {normalizedName || '(invalid name)'}</p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Upload JSON File</label>
          <input
            type="file"
            accept=".json,application/json"
            onChange={handleFileUpload}
            className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm"
          />
          {fileName && <p className="text-xs text-muted-foreground">Loaded: {fileName}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">JSON Content</label>
          <textarea
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            placeholder='{"type":"Feature","geometry":{"type":"Point","coordinates":[-77.03,38.89]},"properties":{}}'
            rows={12}
            className="w-full rounded-md border border-input bg-background p-3 text-xs font-mono resize-y focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        {validation.errors.length > 0 ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
            <p className="text-xs font-medium text-destructive mb-2">Validation errors</p>
            <ul className="list-disc list-inside text-xs text-destructive/90 space-y-1">
              {validation.errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        ) : rawJson.trim() ? (
          <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 text-xs text-green-400">
            JSON is valid and includes geospatial location data.
          </div>
        ) : null}

        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending ? 'Saving...' : 'Save Template'}
        </button>
      </div>
    </div>
  )
}
